/* eslint-disable react/forbid-prop-types */
import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { defaultCouncilFilters } from 'features/CouncilPrequalification/defaultValues';
import { filtersValidationSchema } from 'features/CouncilPrequalification/validation';

import {
  MultipleSelect,
  TextareaInput,
  DatePicker,
  // TextInput,
} from 'components/FormControl';
import { TOAST } from 'constant';
import { toast } from 'layout';

const formOptions = { resolver: yupResolver(filtersValidationSchema) };

function SearchFilters({
  visible,
  setVisible,
  filters,
  setFilters,
  appliedfilters,
  setAppliedFilters,
  // projectTypes,
  // organizations,
  researchFields,
  // researchTypes,
  programs,
  councilTypes,
  // statuses,
}) {
  // #region Data
  const location = useLocation();
  const { t } = useTranslation();

  const { control, handleSubmit, reset, trigger, setValue } = useForm({
    mode: 'onChange',
    defaultValues: { statusName: ['submit', 'approved'] },
    ...formOptions,
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Event
  useEffect(() => {
    reset(filters);
    reset(appliedfilters);
    setValue(
      'timeCreated.start',
      filters?.timeCreated?.start ? new Date(filters?.timeCreated?.start) : ''
    );
    setValue(
      'timeCreated.end',
      filters?.timeCreated?.end ? new Date(filters?.timeCreated?.end) : ''
    );
    setValue(
      'reviewDeadline.start',
      filters?.reviewDeadline?.start ? new Date(filters?.reviewDeadline?.start) : ''
    );
    setValue(
      'timeCreated.end',
      filters?.reviewDeadline?.end ? new Date(filters?.reviewDeadline?.end) : ''
    );
  }, [filters, appliedfilters]);

  const onSubmit = (data) => {
    const modifiedTotalExpenditure = {
      min: data?.totalExpenditure?.min > 0 ? data.totalExpenditure.min : undefined,
      max: data?.totalExpenditure?.max > 0 ? data.totalExpenditure.max : undefined,
    };
    const submitData = { ...data, totalExpenditure: modifiedTotalExpenditure };

    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(submitData));

    setFilters(submitData);
    setAppliedFilters(submitData);
    setVisible(false);
  };

  const handleTimeCreatedChange = async () => {
    await trigger('timeCreated.start');
    await trigger('timeCreated.end');
  };

  const handleReviewDeadlineChange = async () => {
    await trigger('reviewDeadline.start');
    await trigger('reviewDeadline.end');
  };

  const onClear = () => {
    reset({ statusName: ['submit', 'approved'] });
    localStorage.setItem(
      `admin-${location.pathname.slice(1)}-filters`,
      JSON.stringify(defaultCouncilFilters)
    );
    setVisible(false);
    setFilters(defaultCouncilFilters);
    setAppliedFilters(defaultCouncilFilters);
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion Event

  // #region Render
  return (
    <Dialog
      header={<p>{t('projectProposal.viewList.searchFilters')}</p>}
      visible={visible}
      position="center"
      className="w-8 md:w-8 lg:w-8 mx-1rem"
      onHide={() => setVisible(false)}
      draggable={false}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className="p-fluid"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={checkKeyDown}
        autoComplete="off"
      >
        <div className="grid">
          {/* Name */}
          <div className="col-12 md:col-6 lg:col-6">
            <TextareaInput
              label={t('councilList.councilName')}
              name="name"
              control={control}
              errors={errors}
            />
          </div>
          {/* councilTypeId */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('councilList.councilType')}
              name="councilTypeIds"
              options={councilTypes}
              inputField="id"
              renderField="name"
              control={control}
              errors={errors}
            />
          </div>
          {/* researchField */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('councilList.researchField')}
              name="researchFieldIds"
              options={researchFields}
              inputField="id"
              renderField="name"
              control={control}
              errors={errors}
            />
          </div>
          {/* programId */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('councilList.program')}
              name="programIds"
              options={programs}
              inputField="id"
              renderField="name"
              control={control}
              errors={errors}
              panelStyle={{ maxWidth: '680px' }}
            />
          </div>
          {/* timeCreated */}
          <div className="col-12 md:col-6 lg:col-6">
            <label htmlFor="timeCreated.start">{t('councilList.timeCreated')}</label>
            <div className="flex gap-2 flex-wrap mt-2">
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-4rem font-italic">{t('projectProposal.viewList.from')}</span>
                <DatePicker
                  name="timeCreated.start"
                  control={control}
                  errors={errors}
                  onCustomChange={handleTimeCreatedChange}
                />
              </div>
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-4rem font-italic">{t('projectProposal.viewList.to')}</span>
                <DatePicker
                  name="timeCreated.end"
                  control={control}
                  errors={errors}
                  onCustomChange={handleTimeCreatedChange}
                />
              </div>
            </div>
          </div>
          {/* reviewDeadline */}
          <div className="col-12 md:col-6 lg:col-6">
            <label htmlFor="reviewDeadline.start">{t('councilList.reviewDeadline')}</label>
            <div className="flex gap-2 flex-wrap mt-2">
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-4rem font-italic">{t('projectProposal.viewList.from')}</span>
                <DatePicker
                  name="reviewDeadline.start"
                  control={control}
                  errors={errors}
                  onCustomChange={handleReviewDeadlineChange}
                />
              </div>
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-4rem font-italic">{t('projectProposal.viewList.to')}</span>
                <DatePicker
                  name="reviewDeadline.end"
                  control={control}
                  errors={errors}
                  onCustomChange={handleReviewDeadlineChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-content-between">
          <Button
            label={t('projectProposal.viewList.clearFilter')}
            type="button"
            className="w-8rem"
            severity="danger"
            onClick={onClear}
          />

          <div className="flex justify-content-end gap-2">
            <Button
              label={t('formLayout.action.close')}
              type="button"
              className="w-7rem"
              severity="danger"
              onClick={() => setVisible(false)}
            />
            <Button
              label={t('employee.viewList.apply')}
              type="submit"
              className="w-7rem"
              severity="info"
              onClick={checkFormValid}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export default SearchFilters;

SearchFilters.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  appliedfilters: PropTypes.bool.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  // projectTypes: PropTypes.array,
  // // eslint-disable-next-line react/forbid-prop-types
  // organizations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  researchFields: PropTypes.array,
  // // eslint-disable-next-line react/forbid-prop-types, react/forbid-prop-types, react/forbid-prop-types
  // researchTypes: PropTypes.array,
  // // eslint-disable-next-line react/forbid-prop-types
  programs: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  councilTypes: PropTypes.array,
  // // eslint-disable-next-line react/forbid-prop-types
  // statuses: PropTypes.array,
};

SearchFilters.defaultProps = {
  // projectTypes: [],
  // organizations: [],
  researchFields: [],
  // researchTypes: [],
  programs: [],
  councilTypes: [],
  // statuses: [],
};
